import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, forkJoin } from 'rxjs';
import { ErrorLibService } from 'src/shared/services/error-lib.service';
import { ManageUserService } from 'src/shared/services/manage-user.service';
import Swal from 'sweetalert2';
import { Unsubscriber } from '../../unsubscriber/unsubscriber.component';
import {
  Company,
  PaginationInfo,
  Permission,
  PermissionGroup,
  User,
} from '../models/user.model';
import { tzConvertUTC2Local } from 'src/assets/convertTimeZone/convertTimeZone';

// Imports Standalone
import { TableHeaderComponent } from 'src/shared/helper-components/table-header/table-header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { FloatLabel } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { DividerModule } from 'primeng/divider';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';

@Component({
  templateUrl: './manage-user.component.html',
  styleUrls: ['./manage-user.component.less'],
  imports: [
    PageHeaderComponent,
    TableHeaderComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    MatCheckboxModule,
    ButtonModule,
    DrawerModule,
    FloatLabel,
    InputTextModule,
    CheckboxModule,
    MultiSelectModule,
    DividerModule,
    ProgressSpinnerModule,
    TranslateModule,
    DialogModule,
  ],
})
export class ManageUserComponent extends Unsubscriber implements OnInit {
  constructor(
    private manageUserService: ManageUserService,
    private errorLibService: ErrorLibService,
    private formBuilder: FormBuilder,
    private translate: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadCanResetMfa();
    /** Retorna todas as empresas existentes utilizado para preencher o select de empresas na criação e edição de usuários **/
    this.subscriptions = this.manageUserService
      .getAllCompanies()
      .valueChanges.subscribe({
        next: (res: any) => {
          /// Valida se a resposta está carregando ou se não tem dados
          if (res.loading || !res.data) {
            return;
          }

          res?.data?.company?.edges.forEach((company: any) => {
            this.companyList.push(
              new Company(company?.node?.id, company?.node?.name, false),
            );
            this.companyListUpdate.push(
              new Company(company?.node?.id, company?.node?.name, false),
            );
          });
        },
        error: (err: any) => {
          this.errorLibService.errorAlert(err);
          console.log('Error on getAllCompaniesQuery', err);
        },
      });

    /** Retorna todas as permissões existentes utilizado para preencher o select de empresas na criação e edição de usuários **/
    this.subscriptions = this.manageUserService
      .getAllPermission()
      .valueChanges.subscribe({
        next: (res: any) => {
          /// Valida se a resposta está carregando ou se não tem dados
          if (res.loading || !res.data) {
            return;
          }

          res?.data?.availablePermissions?.edges.forEach(
            (availablePermissions: any) => {
              this.permissionList.push(
                new Permission(
                  availablePermissions?.node?.id,
                  availablePermissions?.node?.name,
                  availablePermissions?.node?.codename,
                ),
              );
              this.permissionListUpdate.push(
                new Permission(
                  availablePermissions?.node?.id,
                  availablePermissions?.node?.name,
                  availablePermissions?.node?.codename,
                ),
              );
            },
          );
        },
        error: (err: any) => {
          this.errorLibService.errorAlert(err);
          console.log('Error on availablePermissionsQuery', err);
        },
      });

    /** Retorna todos os grupos de permissões existentes utilizado para preencher o select de empresas na criação e edição de usuários **/
    this.subscriptions = this.manageUserService
      .getAllPermissionGroups()
      .valueChanges.subscribe({
        next: (res: any) => {
          /// Valida se a resposta está carregando ou se não tem dados
          if (res.loading || !res.data) {
            return;
          }

          res?.data?.availableGroups?.edges.forEach((availableGroups: any) => {
            this.permissionGroupList.push(
              new PermissionGroup(
                availableGroups?.node?.id,
                availableGroups?.node?.name,
                availableGroups?.node?.userSet,
              ),
            );
            this.permissionGroupListUpdate.push(
              new PermissionGroup(
                availableGroups?.node?.id,
                availableGroups?.node?.name,
                availableGroups?.node?.userSet,
              ),
            );
          });
        },
        error: (err: any) => {
          this.errorLibService.errorAlert(err);
          console.log('Error on availableGroupsQuery', err);
        },
      });
  }

  /** Variável que contém uma lista de todas as empresas (Utilizado na criação de usuários) **/
  public companyList: Company[] = [];

  /** Variável que contém uma lista de todas as empresas (Utilizado na edição de usuários) **/
  public companyListUpdate: Company[] = [];

  /** Variável que contém uma lista de todas as permissões (Utilizado na criação de usuários) **/
  public permissionList: Permission[] = [];

  /** Variável que contém uma lista de todas as permissões (Utilizado na edição de usuários) **/
  public permissionListUpdate: Permission[] = [];

  /** Variável que contém uma lista de todos os grupos de permissões (Utilizado na criação de usuários) **/
  public permissionGroupList: PermissionGroup[] = [];

  /** Variável que contém uma lista de todos os grupos de permissões (Utilizado na edição de usuários) **/
  public permissionGroupListUpdate: PermissionGroup[] = [];

  /** Variável que contém a lista de usuários (Utilizado para popular a tabela) **/
  public ELEMENT_DATA: User[] = [];

  public filterLoading: boolean = false;

  /**  Renderiza os comandos da lista ELEMENT_DATA para a tabela **/
  public dataSource = new MatTableDataSource<User>(this.ELEMENT_DATA);

  public paginationProperties: PaginationInfo = new PaginationInfo(
    0,
    0,
    false,
    false,
    null,
    null,
  );

  /** Métodos e variáveis responsáveis por configurar a paginação da tabela **/
  @ViewChild(MatPaginator) paginator: MatPaginator;

  /** Variáveis utilizadas para manipulação do Paginator **/
  public pageIndex: number = 0;
  public pageSize: number = 10;
  public length: number = 0;
  public previousPageIndex: number = 0;

  /**variaveis que valida se a senha do usuário está expirada ou não**/
  public expiredPassword = localStorage.getItem('expiredPassword');
  public isExpiredPassword = this.expiredPassword === 'true'; // converte para booleano
  public editTitle = '';

  /** Função que controla os botões de anterior e pŕoximo no paginator **/
  public pageChanged(event: any) {
    /** Armazena o indice da página atual **/
    const newPageIndex = event.first;

    /** Atualiza o valor da página anterior para a página atual **/
    this.previousPageIndex = this.pageIndex;

    /** Atualiza o índice da página atual **/
    this.pageIndex = newPageIndex;

    /**
     * Caso o botão pressionado seja o de página anterior utiliza o
     * startCursor como cursor para o novo filtro
     *
     * Caso o botão pressionado seja o de próxima página é utilizado
     * o endCursor como cursor
     **/
    if (this.previousPageIndex > newPageIndex) {
      this.handlePreviousPage(this.paginationProperties.startCursor);
    } else if (this.previousPageIndex < newPageIndex) {
      this.filterUser(this.paginationProperties.endCursor);
    }
  }

  /** Atualiza a tabela **/
  public setDataSourceAttributes() {
    this.dataSource.data = this.ELEMENT_DATA;
  }

  /** Loading variables **/
  public loadingCreateUser: boolean = false;
  public loadingFilterUser: boolean = false;
  public loadingUser: boolean = false;
  public loadingUpdateUser: boolean = false;

  /** Variável utilizada na manipulação do box de criação de usuários **/
  public isCreateUser: boolean = false;

  /** Variável utilizada na manipulação do box de filtro de usuários **/
  public isFilterUser: boolean = false;

  /** limpar filtro checkbox **/
  public cleaningFilter: Subject<boolean> = new Subject();

  /** Variáveis utilizadas no formulário de filtro **/
  public superUserFilterForm: boolean = false;
  public activeUserFilterForm: boolean = true;
  public usernameFilterForm: FormControl = new FormControl('');
  public emailFilterForm: FormControl = new FormControl('');

  /** Variável para controle do sidebar de edição **/
  public isUpdateUser: boolean = false;

  /** Variável utilizada para manipular o sidebar para edição/visualização **/
  public viewController: boolean = false;

  /** Variável que armazena o id do usuário (utilizado na edição de usuários)**/
  public updateUserId: string = '';

  /** Propriedades utilizadas para manipular quando as opções do checkbox de empresas, permissões e grupos de permissões serão desabilitados **/
  public companiesOptionsDisabled: boolean = false;
  public permissionsOptionsDisabled: boolean = false;
  public permissionsGroupOptionsDisabled: boolean = false;

  /** Utilizado para armazenar a empresa que foi selecionada no checkbox pelo usuário**/
  public companyCheckedList: string[] = [];

  /** Utilizado para armazenar a permissão que foi selecionada pelo usuário**/
  public permissionCheckedList: string[] = [];

  /** Utilizado para armazenar o grupo de permissão que foi selecionado pelo usuário**/
  public permissionsGroupCheckedList: string[] = [];

  // Armazena as empresas, permissões e grupo de permissões já salvos no perfil do usuário
  public originalCompanies: string[] = [];
  public originalPermissions: string[] = [];
  public originalPermissionsGroup: string[] = [];

  /** Variável que armazena os campos do formulário de criação de usuários **/
  public createUserForm: FormGroup = this.formBuilder.group({
    firstNameCreateForm: [null, Validators.required],
    lastNameCreateForm: [null, Validators.required],
    usernameCreateForm: [null, Validators.required],
    emailCreateForm: [null, Validators.required],
    passwordCreateForm: [
      null,
      [
        Validators.required,
        Validators.minLength(6),
        Validators.pattern(/^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])/),
      ],
    ],
    companies: [],
    permissions: [],
    permissionsGroup: [],
    isSuperUser: [false],
  });

  public filterForm: FormGroup = this.formBuilder.group({
    usernameContains: [null],
    emailContains: [null],
    isSuperUser: [false],
  });

  /** Variável que armazena os campos do formulário de update/visualização de usuários **/
  public updateUserForm: FormGroup = this.formBuilder.group({
    userNameUpdateForm: [null, Validators.required],
    firstNameUpdateForm: [null, Validators.required],
    lastNameUpdateForm: [null, Validators.required],
    emailUpdateForm: [null, Validators.required],
    passwordUpdateForm: [
      null,
      [
        Validators.minLength(6),
        Validators.pattern(/^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])/),
      ],
    ],
    superUserUpdateForm: [false],
    isactiveUserUpdateForm: [false],
    companies: [],
    permissions: [],
    permissionsGroup: [],
  });

  /** Método que maninupla a abertura e fechamento do box de criação de usuários **/
  public createUserBox() {
    this.isCreateUser = !this.isCreateUser;
    this.isFilterUser = false;
  }

  /** Método que maninupla a abertura e fechamento do box de filtro de usuários **/
  public filterUserBox() {
    this.isFilterUser = !this.isFilterUser;
    this.isCreateUser = false;
  }

  /** Método que fecha os formulários de filtro e criação de usuários **/
  public closeForms(): void {
    this.isCreateUser = false;
    this.isFilterUser = false;
  }

  /** Método para criação de usuários **/
  public createUser() {
    const companies = this.createUserForm.get('companies')?.value ?? [];
    const permissions = this.createUserForm.get('permissions')?.value ?? [];
    const permissionsGroup =
      this.createUserForm.get('permissionsGroup')?.value ?? [];

    /** Caso todos os campos tenham sidos preenchidos pelo usuário **/
    if (this.createUserForm.valid) {
      this.loadingCreateUser = true;

      /** Realiza a requisição para criação dos usuários **/
      this.subscriptions = this.manageUserService
        .createUser(
          this.createUserForm.get('firstNameCreateForm')?.value,
          this.createUserForm.get('lastNameCreateForm')?.value,
          companies,
          this.createUserForm.get('usernameCreateForm')?.value,
          this.createUserForm.get('passwordCreateForm')?.value,
          this.createUserForm.get('emailCreateForm')?.value,
          this.createUserForm.get('isSuperUser')?.value,
        )
        .subscribe({
          next: (res: any) => {
            /** Armazena o id do usuário que foi criado **/
            const userId = res?.data?.userCreate?.user?.id;

            /** Realiza a requisição para trazer as permissões **/
            this.subscriptions = this.manageUserService
              .setPermission(userId, permissions)
              .subscribe({
                next: () => {},
                error: (error) => {
                  this.errorLibService.errorAlert(error);
                  console.log('Error on setPermission mutation', error);
                },
              });

            /** Realiza a requisição para trazer os grupos de permissões **/
            this.subscriptions = this.manageUserService
              .setPermissionGroup(userId, permissionsGroup)
              .subscribe({
                next: () => {},
                error: (error) => {
                  this.errorLibService.errorAlert(error);
                  console.log('Error on groupSetUser mutation', error);
                },
              });

            this.loadingCreateUser = false;

            /** Exibe mensagem de sucesso ao usuário **/
            Swal.fire({
              title: 'Usuário cadastrado ',
              text: 'Usuário cadastrado com sucesso',
              icon: 'success',
              confirmButtonText: 'Ok',
              cancelButtonText: 'Cancelar',
            });

            /** Fecha o box de registro de usuários **/
            this.closeForms();

            /** Realiza o filtro **/
            this.filterUser(null);

            /** Atualiza a tabela **/
            this.setDataSourceAttributes();

            this.createUserForm.value.companies = [];
            this.createUserForm.value.permissions = [];
            this.createUserForm.value.permissionsGroup = [];
          },
          error: (error) => {
            this.errorLibService.errorAlert(error);
            console.log('Error on updatePermission mutation', error);
            this.loadingCreateUser = false;
          },
        });
    } else {
      /** Caso algum campo não tenha sido preenchido **/
      this.loadingCreateUser = false;
      /** Exibe alerta ao usuário **/
      Swal.fire({
        title: 'Formulário Incompleto!',
        text: 'Preencha todos os campos.',
        icon: 'warning',
        confirmButtonText: 'Ok',
      });
    }
  }

  /** Método que realiza o filtro dos usuários **/
  public filterUser(cursor: string | null) {
    this.filterLoading = true;

    if (cursor == null) {
      this.pageIndex = 0;
    }

    this.subscriptions = this.manageUserService
      .filterUsers(
        this.filterForm?.value?.usernameContains,
        this.filterForm?.value?.emailContains,
        this.filterForm?.value?.isSuperUser,
        cursor,
        this.pageSize,
      )
      ?.valueChanges.subscribe({
        next: (response: any) => {
          /** Valida se a resposta está carregando ou se não tem dados **/
          if (response.loading || !response.data) {
            return;
          }

          this.paginationProperties = new PaginationInfo(
            response.data?.user?.count,
            response.data?.user?.total,
            response.data?.user?.pageInfo?.hasNextPage,
            response.data?.user?.pageInfo?.hasPreviousPage,
            response.data?.user?.pageInfo?.startCursor,
            response.data?.user?.pageInfo?.endCursor,
          );

          this.ELEMENT_DATA = [];

          //this.isReady = false;
          this.length = this.paginationProperties?.total;

          response?.data?.user?.edges.forEach((user: any) => {
            let permissions: Permission[] = [];
            user?.node?.userPermissions?.edges.forEach((permission: any) => {
              permissions.push(
                new Permission(
                  permission?.node?.id,
                  permission?.node?.reference,
                  permission?.node?.codeName,
                  permission?.node?.checked,
                ),
              );
            });

            let permissionsGroups: PermissionGroup[] = [];
            user?.node?.groups?.edges.forEach((permissionsGroup: any) => {
              permissionsGroups.push(
                new PermissionGroup(
                  permissionsGroup?.node?.id,
                  permissionsGroup?.node?.reference,
                  permissionsGroup?.node?.userSet,
                  permissionsGroup?.node?.checked,
                ),
              );
            });

            let companies: Array<Company> = [];
            user?.node?.companies?.edges.forEach((company: any) => {
              companies.push(
                new Company(company?.node?.id, company?.node?.name),
              );
            });

            let name = user?.node?.firstName + ' ' + user?.node?.lastName;
            let superUser = user?.node?.isSuperuser;
            this.ELEMENT_DATA.push(
              new User(
                user?.node?.id,
                user?.node?.username,
                name,
                user?.node?.firstName,
                user?.node?.lastName,
                user?.node?.email,
                //Converter para fuso horário baseado no navegador do usuário
                tzConvertUTC2Local(user?.node?.lastLogin),
                superUser,
                companies,
                permissions,
                permissionsGroups,
                user?.node?.isActive,
                !!user?.node?.mfaEnabled,
              ),
            );
          });

          if (this.ELEMENT_DATA.length === 0) {
            Swal.fire({
              title: 'Sua pesquisa não retornou resultados',
              icon: 'warning',
              confirmButtonText: 'Ok',
            });
          }

          this.setDataSourceAttributes();

          this.filterLoading = false;
        },
      });
  }

  public handlePreviousPage(cursor: string | null): void {
    if (cursor == null) {
      this.pageIndex = 0;
    }

    this.subscriptions = this.manageUserService
      .handlePreviousPage(
        this.filterForm?.value?.usernameContains,
        this.filterForm?.value?.emailContains,
        this.filterForm?.value?.isSuperUser,
        cursor,
        this.pageSize,
      )
      .valueChanges.subscribe({
        next: (response: any) => {
          /// Valida se a resposta está carregando ou se não tem dados
          if (response.loading || !response.data) {
            return;
          }

          this.paginationProperties = new PaginationInfo(
            response?.data?.user?.count,
            response?.data?.user?.total,
            response?.data?.user?.pageInfo?.hasNextPage,
            response?.data?.user?.pageInfo?.hasPreviousPage,
            response?.data?.user?.pageInfo?.startCursor,
            response?.data?.user?.pageInfo?.endCursor,
          );

          this.ELEMENT_DATA = [];
          //this.isReady = false;
          this.length = this.paginationProperties?.total;

          response?.data?.user?.edges.forEach((user: any) => {
            let companies: Array<Company> = [];
            user?.node?.companies?.edges.forEach((company: any) => {
              companies.push(
                new Company(company?.node?.id, company?.node?.name),
              );
            });

            let permissions: Permission[] = [];
            user?.node?.userPermissions?.edges.forEach((permission: any) => {
              permissions.push(
                new Permission(
                  permission?.node?.id,
                  permission?.node?.reference,
                  permission?.node?.codeName,
                  permission?.node?.checked,
                ),
              );
            });

            let permissionsGroups: PermissionGroup[] = [];
            user?.node?.groups?.edges.forEach((permissionsGroup: any) => {
              permissionsGroups.push(
                new PermissionGroup(
                  permissionsGroup?.node?.id,
                  permissionsGroup?.node?.reference,
                  permissionsGroup?.node?.userSet,
                  true,
                ),
              );
            });

            let name = user?.node?.firstName + ' ' + user?.node?.lastName;
            let superUser = user?.node?.isSuperuser;
            this.ELEMENT_DATA.push(
              new User(
                user?.node?.id,
                user?.node?.username,
                name,
                user?.node?.firstName,
                user?.node?.lastName,
                user?.node?.email,
                //Converter para fuso horário baseado no navegador do usuário
                tzConvertUTC2Local(user?.node?.lastLogin),
                superUser,
                companies,
                permissions,
                permissionsGroups,
                user?.node?.isActive,
                !!user?.node?.mfaEnabled,
              ),
            );

            this.setDataSourceAttributes();

            if (this.ELEMENT_DATA.length === 0) {
              Swal.fire({
                title: 'Sua pesquisa não retornou resultados',
                icon: 'warning',
                confirmButtonText: 'Ok',
              });
            }
          });
        },
      });
  }

  /** Utilizado no botão de cancelar que fecha o box de filtro **/
  public cancelCreateOrFilter() {
    this.isCreateUser = false;
    this.isFilterUser = false;
    this.usernameFilterForm.reset();
    this.emailFilterForm.reset();
    this.createUserForm.value.companies = [];
    this.createUserForm.value.permissions = [];
    this.createUserForm.value.permissionsGroup = [];
  }

  /** Método para fechar o sidebar de edição de usuários **/
  public closeSidebarUpdate() {
    this.isUpdateUser = false;
  }

  /** Método utilizado para preencher os campos do formulário de edição e visualização **/
  public OpenSidebarUpdate(element: User, isViewing: boolean) {
    /** Abre o sidebar de edição **/
    this.isUpdateUser = true;

    /** Variável utilizada para manipular o sidebar de edição/visualização **/
    this.viewController = isViewing;

    /** Caso for clicado no botão de visualização o formulário é desabilitado para edição **/
    if (isViewing === true) {
      this.updateUserForm.disable();
      this.editTitle = 'manage-user.view-title';
      this.updateUserForm.controls['companies'].enable();
      this.updateUserForm.controls['permissions'].enable();
      this.updateUserForm.controls['permissionsGroup'].enable();

      /** Caso for clicado no botão de edição, habilita o formulário para edição **/
    } else {
      this.updateUserForm.enable();
      this.updateUserForm.controls['userNameUpdateForm'].disable();
      this.editTitle = 'manage-user.edit-title';

      // Atualiza o estado do campo desabilitado
      this.permissionListUpdate.forEach((permission) => {
        permission.disabled = false;
      });

      this.permissionGroupListUpdate.forEach((permissionGroup) => {
        permissionGroup.disabled = false;
      });
    }

    /** armazena o id do usuário selecionado **/
    this.updateUserId = element.id;

    // Captura os ids das empresas retornadas no cadastro do usuário
    const companies = element.companies.map((company) => company.id);

    // Cria uma nova lista com as empresas, guardando o histórico as empresas no estado atual
    this.originalCompanies = [...companies];

    this.updateCompanyOptions();

    // Guarda o histórico das permissões atuais
    this.originalPermissions = element.permissions.map(
      (permission) => permission.id,
    );

    // Guarda o histórico do grupo de permissões atuais
    this.originalPermissionsGroup = element.permissionsGroups.map(
      (permissionGroup) => permissionGroup.id,
    );

    /** Setando os valores dos campos do formulário de edição/visualização **/
    this.updateUserForm.setValue({
      userNameUpdateForm: element.username,
      firstNameUpdateForm: element.firstName,
      lastNameUpdateForm: element.lastName,
      emailUpdateForm: element.email,
      passwordUpdateForm: '',
      superUserUpdateForm: element.superuser,
      isactiveUserUpdateForm: element.isActive,
      companies: companies,
      permissions: this.originalPermissions,
      permissionsGroup: this.originalPermissionsGroup,
    });
  }

  // Método utilizado na atualização das empresas, permitindo selecionar apenas uma unica empresa e manter as empresas já cadastradas no perfil do usuário
  public updateCompanyOptions(): void {
    // Caso o usuário clique na opção de visualizar os dados do usuário
    if (this.viewController) {
      // Desabilita a seleção das opções de empresa
      this.companyListUpdate.forEach((company) => {
        company.disabled = true;
      });

      // Desabilita a seleção das opções de permissões
      this.permissionListUpdate.forEach((permission) => {
        permission.disabled = true;
      });

      // Desabilita a seleção das opções de grupos de permissões
      this.permissionGroupListUpdate.forEach((permissionGroup) => {
        permissionGroup.disabled = true;
      });

      return;
    }

    // Armazena a empresa selecionada pelo usuário
    const selectedCompanies = this.updateUserForm.get('companies')?.value ?? [];

    // Captura as empresas diferentes das empresas já existentes no cadastro do usuário
    const addedCompanies = selectedCompanies.filter(
      (id: string) => !this.originalCompanies.includes(id),
    );

    // Limita a quantidade de empresas a serem adicionadas na seleção de empresas
    const limitReached = addedCompanies.length >= 1;

    // Desativa a seleção de outras empresas caso uma unica empresa estiver selecionada
    this.companyListUpdate.forEach((company) => {
      company.disabled =
        limitReached && !selectedCompanies.includes(company.id);
    });
  }

  /** Método utilizado para (des)ativação de usuários **/
  public activateUser() {
    let selectedUser = this.ELEMENT_DATA.filter(
      (user) => user.id === this.updateUserId,
    );
    let password = this.updateUserForm.get('passwordUpdateForm')?.value;

    if (this.updateUserForm.get('isactiveUserUpdateForm')?.value == true) {
      if (
        this.updateUserForm.get('isactiveUserUpdateForm')?.value !=
        selectedUser[0].isActive
      ) {
        if (password) {
          this.subscriptions = this.manageUserService
            .userActivation(selectedUser[0].id, password)
            .subscribe({
              next: () => {
                /** Atualiza a tabela com as informações do usuário**/
                this.filterUser(null);
                this.setDataSourceAttributes();
              },
            });
        }
      }
    } else {
      this.subscriptions = this.manageUserService
        .userDeactivation(selectedUser[0].id)
        .subscribe({
          next: () => {
            this.filterUser(null);
            this.setDataSourceAttributes();
          },
        });
    }
  }

  // ---------- Reset de 2FA ----------

  /** Codename da permissão que libera o reset de 2FA (backend: users.reset_user_mfa) **/
  private readonly resetMfaPermission = 'reset_user_mfa';

  /** O botão de reset só aparece para quem tem a permissão **/
  public canResetMfa = false;

  /** Estado do modal de confirmação **/
  public isResetMfaDialogOpen = false;
  public loadingResetMfa = false;
  public resetMfaTarget: User | null = null;

  /** Consulta as permissões do usuário logado e define se o botão de reset é exibido **/
  private loadCanResetMfa(): void {
    this.subscriptions = this.manageUserService
      .getLoggedUserPermissions(localStorage.getItem('username'))
      .subscribe({
        next: (res: any) => {
          const node = res?.data?.user?.edges?.[0]?.node;
          const permissions: string[] = node?.allPermissions ?? [];
          this.canResetMfa =
            !!node?.isSuperuser ||
            permissions.includes(this.resetMfaPermission);
        },
        error: () => {
          // Sem a resposta, o botão simplesmente não é exibido
          this.canResetMfa = false;
        },
      });
  }

  /** Abre o modal de confirmação para o usuário da linha clicada **/
  public openResetMfa(user: User): void {
    this.resetMfaTarget = user;
    this.isResetMfaDialogOpen = true;
  }

  /** Fecha o modal (bloqueado enquanto a requisição está em andamento) **/
  public closeResetMfa(): void {
    if (this.loadingResetMfa) {
      return;
    }
    this.isResetMfaDialogOpen = false;
  }

  /** Confirma o reset do 2FA do usuário selecionado **/
  public confirmResetMfa(): void {
    if (!this.resetMfaTarget || this.loadingResetMfa) {
      return;
    }

    const target = this.resetMfaTarget;
    this.loadingResetMfa = true;

    this.subscriptions = this.manageUserService
      .userMfaReset(target.id)
      .subscribe({
        next: () => {
          this.loadingResetMfa = false;
          this.isResetMfaDialogOpen = false;

          Swal.fire({
            icon: 'success',
            title: this.translate.instant(
              'manage-user.reset-mfa-success-title',
            ),
            text: this.translate.instant('manage-user.reset-mfa-success-text', {
              username: target.username,
            }),
            confirmButtonText: 'Ok',
          });
        },
        error: (error) => {
          this.loadingResetMfa = false;
          this.errorLibService.errorAlert(error);
          console.log('Error on userMfaReset mutation', error);
        },
      });
  }

  /** Método utilizado para edição de usuários **/
  public updateUser() {
    const companies = this.updateUserForm.get('companies')?.value ?? [];

    // Armazena a permissões selecionadas pelo usuário
    const selectedPermissions =
      this.updateUserForm.get('permissions')?.value ?? [];

    // Armazena os grupos de permissões selecionadas pelo usuário
    const selectedPermissionsGroup =
      this.updateUserForm.get('permissionsGroup')?.value ?? [];

    if (
      this.updateUserForm.get('isactiveUserUpdateForm')?.value == true &&
      !this.updateUserForm.get('passwordUpdateForm')?.value &&
      !this.ELEMENT_DATA.filter((user) => user.id === this.updateUserId)[0]
        .isActive
    ) {
      Swal.fire({
        title: 'Informe uma senha para reativar o usuário.',
        text: 'Ele será obrigado e modifica-la após o primeiro acesso',
        icon: 'warning',
        confirmButtonText: 'Ok',
      });
      throw 'exit';
    }

    /** Realiza a requisição de edição de usuários caso todos os campos obrigatórios do formulário estejam preenchidos **/
    if (this.updateUserForm.valid) {
      this.loadingUpdateUser = true;

      // variável da requisiçao de atualização do usuário
      const updatedUserRequest = this.manageUserService.updateUser(
        this.updateUserId,
        companies,
        this.updateUserForm.get('firstNameUpdateForm')?.value,
        this.updateUserForm.get('lastNameUpdateForm')?.value,
        this.updateUserForm.get('emailUpdateForm')?.value,
        this.updateUserForm.get('superUserUpdateForm')?.value,
        this.isExpiredPassword,
        this.updateUserForm.get('passwordUpdateForm')?.value,
      );

      // Armazena as novas permissões adicionadas
      const permissionsToAdd = selectedPermissions.filter(
        (id: string) => !this.originalPermissions.includes(id),
      );

      // Armazena as permissões desmarcadas (para serem removidas)
      const permissionsToRemove = this.originalPermissions.filter(
        (id) => !selectedPermissions.includes(id),
      );

      /** Lista responsavel de agrupar  para esperar que todas as requisições sejam realizadas  **/
      const requests = [
        updatedUserRequest,
        this.manageUserService.setPermission(
          this.updateUserId,
          permissionsToAdd,
        ) /** Realiza a requisição para setar as permissões **/,
        this.manageUserService.setPermissionGroup(
          this.updateUserId,
          selectedPermissionsGroup,
        ) /** Realiza a requisição para setar os grupos de permissões **/,
      ];

      if (permissionsToRemove.length > 0) {
        requests.push(
          this.manageUserService.removePermission(
            this.updateUserId,
            permissionsToRemove,
          ),
        );
      }

      /** Realiza a requisição de edição de usuários **/
      this.subscriptions = forkJoin(requests).subscribe({
        /** Caso a requisição seja realizada com sucesso **/
        next: () => {
          /** Chama a função de (des)ativar os usuários**/
          this.activateUser();

          this.loadingUpdateUser = false;

          /** Exibe mensagem de sucesso ao usuário **/
          Swal.fire({
            title: 'Usuário Atualizado ',
            text: 'Usuário Atualizado com sucesso',
            icon: 'success',
            confirmButtonText: 'Ok',
            cancelButtonText: 'Cancelar',
          });

          /** Atualiza a tabela com os dados alterados **/
          this.filterUser(null);

          this.setDataSourceAttributes();

          /** Fecha o sidebar de edição **/
          this.isUpdateUser = false;
        },
        /** Caso ocorra algum erro para realizar o update de usuários **/
        error: (error) => {
          this.loadingUpdateUser = false;
          /** Exibe mensagem de erro **/
          this.errorLibService.errorAlert(error);
          console.log('Error on updateUser mutatiton', error);
        },
      });
    } else {
      /** Caso algum campo obrigatório do formulário não tenha sido preenchido **/
      Swal.fire({
        title: 'Formulário Incompleto!',
        text: 'Preencha todos os campos.',
        icon: 'warning',
        confirmButtonText: 'Ok',
      });
    }
  }
}
